import { useEffect, useState, useCallback, useMemo } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SafetyBadge, DiffView, ReliabilityBadge, ConfidenceBar, Spinner } from "@/components/bits";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2, XCircle, UploadCloud, RefreshCw, ShieldCheck, FileWarning, Search, Layers } from "lucide-react";

const PUBLISHABLE_FROM = ["APPROVED"];
const STATUS_OPTIONS = ["ALL", "READY", "APPROVED", "MANUAL_EDIT_REQUIRED", "MANUAL_VERIFICATION_REQUIRED", "BLOCKED_CONFLICT", "NO_CHANGE", "PUBLISHED_VERIFIED", "SIMULATED_DRY_RUN", "REJECTED"];

export default function Review() {
  const { user, mode } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [dedupe, setDedupe] = useState(true);

  const filteredList = useMemo(() => {
    let items = list;
    if (statusFilter !== "ALL") items = items.filter((p) => p.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) items = items.filter((p) => `${p.post_title} ${p.query} ${p.post_id}`.toLowerCase().includes(q));
    if (dedupe) {
      const seen = new Set();
      items = items.filter((p) => {
        const key = `${p.post_id}|${p.status}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return items;
  }, [list, statusFilter, search, dedupe]);

  const loadList = useCallback(async () => {
    const { data } = await client.get("/proposals");
    setList(data);
    return data;
  }, []);

  const loadOne = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await client.get(`/proposals/${id}`);
      setActive(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const data = await loadList();
      const saved = localStorage.getItem("ez_active_proposal");
      if (saved && data.find((p) => p.id === saved)) loadOne(saved);
      else if (data[0]) loadOne(data[0].id);
    })();
  }, [loadList, loadOne]);

  const act = async (action) => {
    setBusy(true);
    try {
      const payload = (action === "approve" || action === "reject") ? { note } : {};
      const { data } = await client.post(`/proposals/${active.id}/${action}`, payload);
      if (data.status === "CONTENT_CHANGED") {
        toast.error(data.message || "WordPress content changed");
      } else if (data.status === "PUBLISHED_VERIFIED") {
        toast.success("Published + Verified");
      } else if (data.status === "SIMULATED_DRY_RUN") {
        toast.success("Dry run simulated — pre/post hash checks passed");
      } else if (data.status === "PUBLISH_VERIFICATION_FAILED") {
        toast.error("Publish verification failed");
      } else {
        toast.success(`Proposal ${action}d`);
      }
      setNote("");
      await loadOne(active.id);
      await loadList();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const a = active?.analysis || {};
  const canApprove = user?.permissions?.approve;
  const canPublish = user?.permissions?.publish;

  return (
    <div className="max-w-7xl">
      <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Approval & Publish</p>
      <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1 mb-6">Change Review</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-4 space-y-2">
          <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="review-filters">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input data-testid="review-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search post title, query or ID…" className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-2 text-sm outline-none focus:border-blue-500" />
            </div>
            <div className="flex items-center gap-2">
              <select data-testid="review-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex-1 rounded-lg border border-slate-200 px-2 py-2 text-xs outline-none focus:border-blue-500">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === "ALL" ? "All statuses" : s.replace(/_/g, " ")}</option>)}
              </select>
              <button data-testid="review-dedupe-toggle" onClick={() => setDedupe((d) => !d)} title="Collapse duplicate proposals for the same post" className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold ${dedupe ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>
                <Layers className="h-3.5 w-3.5" /> Dedupe
              </button>
            </div>
            <div className="text-[11px] text-slate-400 px-0.5">{filteredList.length} of {list.length} shown</div>
          </div>
          {filteredList.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500">{list.length === 0 ? "No proposals yet. Create one from AI Research." : "No proposals match your filters."}</div>}
          {filteredList.map((p) => (
            <button
              key={p.id}
              data-testid="proposal-list-item"
              onClick={() => loadOne(p.id)}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${active?.id === p.id ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <div className="font-semibold text-slate-800 text-sm truncate">{p.post_title}</div>
              <div className="text-xs text-slate-400 font-mono mb-2">{p.query} · #{p.post_id}</div>
              <SafetyBadge status={p.status} />
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-8">
          {loading && <Spinner label="Loading proposal…" />}
          {active && !loading && (
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="font-serif text-xl font-bold text-slate-900">{active.post_title}</h2>
                    <a href={active.post_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">Post #{active.post_id} <ExternalLink className="h-3 w-3" /></a>
                  </div>
                  <SafetyBadge status={active.status} />
                </div>
                {active.portion_scope && <div className="mt-3 rounded bg-indigo-50 border border-indigo-200 px-3 py-1.5 text-xs text-indigo-700">Scoped to a selected portion of the post.</div>}
                <p className="text-sm text-slate-600 mt-3">{a.reason}</p>
              </div>

              {active.conflict && (
                <div className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
                  <b>Blocked — Source Conflict.</b> {active.conflict_reason} Drafting is allowed but publishing is disabled.
                </div>
              )}

              {active.status === "NO_CHANGE" && (
                <div className="rounded-xl border border-slate-300 bg-slate-50 p-6 text-center">
                  <p className="font-serif text-lg font-bold text-slate-700">No Verified Change Detected</p>
                  <p className="text-sm text-slate-500 mt-1">The post already reflects the verified facts. Nothing was fabricated.</p>
                </div>
              )}

              {a.change_detected && (
                <div data-testid="fact-proposal-card" className="rounded-xl border border-slate-200 bg-white p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Factual Proposal</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                      <div className="text-[11px] font-mono uppercase text-rose-700 mb-1">Existing Value</div>
                      <div className="text-sm font-semibold text-slate-800">{a.existing_value}</div>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                      <div className="text-[11px] font-mono uppercase text-emerald-700 mb-1">Proposed Value</div>
                      <div className="text-sm font-semibold text-slate-800">{a.proposed_value}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div><div className="text-slate-400 uppercase font-mono mb-1">Source</div>{a.source_url ? <a href={a.source_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{a.source}</a> : a.source}</div>
                    <div><div className="text-slate-400 uppercase font-mono mb-1">Type</div>{a.source_type}</div>
                    <div><div className="text-slate-400 uppercase font-mono mb-1">Reliability</div><ReliabilityBadge level={a.reliability} /></div>
                    <div><div className="text-slate-400 uppercase font-mono mb-1">Confidence</div><ConfidenceBar value={a.confidence} /></div>
                  </div>
                  {a.evidence_quote && <p className="mt-3 text-xs italic text-slate-500 border-l-2 border-slate-200 pl-2">"{a.evidence_quote}"</p>}
                </div>
              )}

              {active.protected_blocks?.length > 0 && (
                <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 flex items-start gap-3">
                  <FileWarning className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="text-sm text-orange-800"><b>Manual Edit Required.</b> The change touches protected/fragile block(s): {active.protected_blocks.join(", ")}. Auto-patching is disabled.</div>
                </div>
              )}
              {active.validation_error && !active.diff && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800"><b>Manual Review Required.</b> {active.validation_error}</div>
              )}

              {active.diff && (
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Smallest Safe Patch — Visual Diff</p>
                    <span data-testid="pre-publish-content-hash-badge" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-mono text-slate-600" title={active.baseline_hash}>
                      <ShieldCheck className="h-3 w-3" /> baseline {active.baseline_hash?.slice(0, 10)}…
                    </span>
                  </div>
                  <DiffView diff={active.diff} />
                </div>
              )}

              {active.verification && (
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Post-Publish Verification</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    {Object.entries(active.verification).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2">
                        {typeof v === "boolean" ? (v ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />) : <ShieldCheck className="h-4 w-4 text-slate-400" />}
                        <span className="text-xs text-slate-600 font-mono">{k}: {String(v).slice(0, 14)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
                {active.approval_note && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <span className="text-[11px] font-mono uppercase text-slate-400 mr-2">Reviewer note</span>“{active.approval_note}”
                  </div>
                )}
                {active.status === "READY" && (
                  <textarea
                    data-testid="approval-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional: leave a note explaining your approval or rejection (recorded in the audit log)…"
                    rows={2}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 resize-none"
                  />
                )}
                <div className="flex flex-wrap items-center gap-3">
                {active.status === "READY" && (
                  <>
                    <button data-testid="approve-patch-btn" disabled={!canApprove || busy} onClick={() => act("approve")} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40">
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </button>
                    <button data-testid="reject-patch-btn" disabled={!canApprove || busy} onClick={() => act("reject")} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-rose-400 hover:text-rose-600 disabled:opacity-40">
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                    {!canApprove && <span className="text-xs text-slate-400">Your role cannot approve.</span>}
                  </>
                )}
                {PUBLISHABLE_FROM.includes(active.status) && (
                  <button data-testid="publish-wp-api-btn" disabled={!canPublish || busy || !mode || mode === "UNKNOWN"} onClick={() => { if (mode === "LIVE") { setConfirmText(""); setShowConfirm(true); } else { act("publish"); } }} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 ${mode === "LIVE" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`}>
                    <UploadCloud className="h-4 w-4" /> {mode === "LIVE" ? "Publish to WordPress" : mode === "DRY_RUN" ? "Simulate Publish (Dry Run)" : "Checking mode…"}
                  </button>
                )}
                {active.status === "CONTENT_CHANGED" && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-rose-700">WordPress content changed since this proposal was created.</span>
                    <button data-testid="refetch-verify-btn" onClick={() => (window.location.href = "/research")} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-500"><RefreshCw className="h-4 w-4" /> Refresh & Re-Analyze</button>
                  </div>
                )}
                {["PUBLISHED_VERIFIED", "SIMULATED_DRY_RUN"].includes(active.status) && (
                  <span className="text-sm text-emerald-700 font-semibold">Done · published by {active.published_by} in {active.mode || mode} mode.</span>
                )}
                {!canPublish && PUBLISHABLE_FROM.includes(active.status) && <span className="text-xs text-slate-400">Your role cannot publish (needs Approver/Admin).</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div data-testid="live-publish-confirm-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && setShowConfirm(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-rose-600 mb-2">
              <UploadCloud className="h-5 w-5" />
              <h3 className="font-serif text-lg font-bold text-slate-900">Publish to the LIVE site?</h3>
            </div>
            <p className="text-sm text-slate-600">You are about to write a real change to <b>{active?.post_title}</b> (post #{active?.post_id}) on the live WordPress site. This cannot be auto-undone.</p>
            <p className="text-sm text-slate-600 mt-3">Type <span className="font-mono font-bold text-slate-900">CONFIRM</span> to proceed:</p>
            <input
              data-testid="live-publish-confirm-input"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-rose-500"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="live-publish-cancel-btn" disabled={busy} onClick={() => setShowConfirm(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40">Cancel</button>
              <button
                data-testid="live-publish-confirm-btn"
                disabled={busy || confirmText.trim().toUpperCase() !== "CONFIRM"}
                onClick={async () => { setShowConfirm(false); await act("publish"); }}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-40"
              >
                <UploadCloud className="h-4 w-4" /> Publish now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
